import React, { useState, useEffect, useRef } from 'react';
import { get, isEmpty } from 'lodash';
import { connect } from 'react-redux';

import { apiResponseCodes, phoneNumberMaxLength, regex } from '../../constants/globals';
import { constants } from '../../constants/configuration';
import routes from '../../routes';

import asyncRoute from '../../helpers/hoc/asyncRoute';
import {
  attemptLoginAction,
  sendOtpAction,
  updateProfileAction,
  addBrandGuestAction,
  dispatchIsRewardsClubFlowAction,
} from '../../actions/app.action';

import { fetchAppStartupInfoAction, clearApiErrorResponseAction } from '../../actions/common';

import { extractWhitelabelSubdomain } from '../../utils/helpers';

import './RewardsClubSignup.scss';

const TextField = asyncRoute(() => import('../../components/MaterialElements/TextField'));
const CheckBox = asyncRoute(() => import('../../components/MaterialElements/Checkbox'));
const Button = asyncRoute(() => import('../../components/MaterialElements/Button'));
const DatePicker = asyncRoute(() => import('../../components/MaterialElements/DatePicker'));
const AuthenticationPopup = asyncRoute(() => import('../../components/Popup/AuthenticationPopup'));
const ExceptionComponent = asyncRoute(() => import('../../components/ExceptionComponent'));

const nameValidator = new RegExp(regex.nameValidator);
const emailValidator = new RegExp(regex.emailValidator);

const RewardsClubSignup = ({
  history,
  brand,
  fetchAppStartupInfo,
  sendOtp,
  attemptLogin,
  updateProfile,
  addBrandGuest,
  dispatchIsRewardsClubFlow,
  errorResponse,
  clearApiErrorResponse,
}) => {
  const [formState, setFormState] = useState({
    name: '',
    phoneNumber: '',
    email: '',
    dob: '',
    consent: true,
  });
  const [showNameValidatorError, setShowNameValidatorError] = useState(false);
  const [showPhoneNumberValidatorError, setShowPhoneNumberValidatorError] = useState(false);
  const [showEmailValidatorError, setShowEmailValidatorError] = useState(false);
  const [showDOBValidatorError, setShowDOBValidatorError] = useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const [showVerifyOTPPopup, setShowVerifyOTPPopup] = useState(false);
  const [showOTPErrorPopup, setShowOTPErrorPopup] = useState(false);

  const phoneInputRef = useRef(null);

  useEffect(() => {
    // Reset any API errors
    clearApiErrorResponse();

    const subDomain = extractWhitelabelSubdomain();

    if (subDomain) {
      if (isEmpty(brand)) {
        const appStartupReqBody = {
          subdomain: subDomain,
          includeModules: ['engage', 'brand'],
        };
        fetchAppStartupInfo({ appStartupReqBody }).then((response) => {});
      }
    }

    return () => {
      clearApiErrorResponse();
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(errorResponse)) {
      const { code, status, message } = errorResponse;

      code === 'OTP_INVALID' && setShowOTPErrorPopup(true);
    }
  }, [errorResponse]);

  const handleFieldChange = (event) => {
    const { name, value } = event.target;

    setFormState({ ...formState, [name]: value });
  };

  const handleFieldBlur = (event) => {
    const { name: nodeName } = event.target;
    const { name, phoneNumber, email } = formState;
    nodeName === 'name' && validateName(name);
    nodeName === 'phoneNumber' && validatePhone(phoneNumber);
    nodeName === 'email' && validateEmail(email);
  };

  const handleDOBChange = (value) => {
    setFormState({ ...formState, dob: value });
    validateDOB(value);
  };

  const handleConsentCheckChange = (event) => {
    const { checked } = event.target;
    setFormState({ ...formState, consent: !formState.consent });
    setShowConsentError(!checked);
  };

  const handleFormSubmit = async () => {
    const { name, phoneNumber, email, dob, consent } = formState;

    const isFormInvalid = [
      validateName(name),
      validatePhone(phoneNumber),
      validateEmail(email),
      validateDOB(dob),
      !!consent,
    ].some((pass) => pass === false);

    setShowConsentError(consent ? false : true);

    if (isFormInvalid) return;

    const sendOTPResponse = await sendOtp(phoneNumber, constants.defaultCountryCode);
    if (!apiResponseCodes.SUCCESS.includes(get(sendOTPResponse, 'status'))) return;

    setShowVerifyOTPPopup(true);
  };

  const validateName = (name) => {
    const pass = !!name && nameValidator.test(name);
    setShowNameValidatorError(pass ? false : true);
    return pass;
  };

  const validatePhone = (phone) => {
    const pass = !!phone && phone.length === 10;
    setShowPhoneNumberValidatorError(pass ? false : true);
    return pass;
  };

  const validateEmail = (email) => {
    const pass = !!email && emailValidator.test(email);
    setShowEmailValidatorError(pass ? false : true);
    return pass;
  };

  const validateDOB = (dob) => {
    const pass = !!dob;
    setShowDOBValidatorError(pass ? false : true);
    return pass;
  };

  const handlePhoneNumberChangeClick = () => {
    phoneInputRef && phoneInputRef.current && phoneInputRef.current.focus();
    setShowVerifyOTPPopup(false);
  };

  const handleOTPVerify = async (otp) => {
    const loginReqBody = {
      phoneNumber: formState.phoneNumber,
      otp,
      countryCode: constants.defaultCountryCode,
    };
    const loginAPIResponse = await attemptLogin(loginReqBody);
    if (!apiResponseCodes.SUCCESS.includes(get(loginAPIResponse, 'status'))) return;

    setShowVerifyOTPPopup(false);

    const updateProfileReqBody = {
      name: formState.name,
      email: formState.email,
      dob: formState.dob ? formState.dob.format('YYYY-MM-DD') : '',
    };
    const guestId = get(loginAPIResponse, 'data.guest.id', '');
    const updateGuestAPIResponse = await updateProfile(updateProfileReqBody, guestId);
    if (!apiResponseCodes.SUCCESS.includes(get(updateGuestAPIResponse, 'status'))) return;

    const addBrandGuestReqBody = {
      guestId,
      brandId: get(brand, 'brandId', ''),
      isUserConsent: formState.consent,
      dob: formState.dob ? formState.dob.format('YYYY-MM-DD') : '',
    };
    const addBrandGuestAPIResponse = await addBrandGuest({ addBrandGuestReqBody });
    if (!apiResponseCodes.SUCCESS.includes(get(addBrandGuestAPIResponse, 'status'))) return;

    dispatchIsRewardsClubFlow(true);

    history.push(`/${routes.containers.deals}`);
  };

  const handleOTPErrorPopupClose = () => {
    clearApiErrorResponse();
    setShowOTPErrorPopup(false);
  };

  const handleOTPErrorPopupConfirm = () => {
    clearApiErrorResponse();
    setShowOTPErrorPopup(false);
  };

  const brandName = get(brand, 'name', '');

  return (
    <div className="rewards-club-signup">
      <div className="rewards-club-signup-form-header">
        <div className="brand-logo">
          <img src={get(brand, 'logoUrl', '')} alt="" />
        </div>
        <h2 className="rewards-club-header">REWARDS CLUB</h2>
      </div>
      <div className="rewards-club-signup-form-wrapper">
        <div className="rewards-club-signup-form">
          <div className="rewards-club-signup-form_name-form-field">
            <TextField
              id="rewards-club-signup-form_name-form-field"
              type="text"
              name="name"
              label="Name"
              placeholder="Enter your name"
              maxLength={50}
              value={formState.name}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={showNameValidatorError}
              autoFocus
            />
            {showNameValidatorError && (
              <div className="rewards-club-signup-form_form-field-error">
                Please enter a valid name
              </div>
            )}
          </div>
          <div className="rewards-club-signup-form_phone-form-field">
            <TextField
              id="rewards-club-signup-form_phone-form-field"
              inputRef={phoneInputRef}
              type="tel"
              name="phoneNumber"
              label="Mobile no"
              placeholder="Enter your mobile no"
              value={formState.phoneNumber}
              maxLength={phoneNumberMaxLength}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={showPhoneNumberValidatorError}
            />
            {showPhoneNumberValidatorError && (
              <div className="rewards-club-signup-form_form-field-error">
                Please enter a valid phone number
              </div>
            )}
          </div>
          <div className="rewards-club-signup-form_email-form-field">
            <TextField
              id="rewards-club-signup-form_email-form-field"
              type="email"
              name="email"
              label="Email ID"
              placeholder="Enter your email id"
              value={formState.email}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={showEmailValidatorError}
            />
            {showEmailValidatorError && (
              <div className="rewards-club-signup-form_form-field-error">
                Please enter a valid email
              </div>
            )}
          </div>
          <div className="rewards-club-signup-form_dob-form-field">
            <label className="rewards-club-signup-form_dob-form-field_label">Date of Birth</label>
            <DatePicker
              value={formState.dob}
              clearable={true}
              onChange={handleDOBChange}
              onBlur={handleFieldBlur}
              error={showDOBValidatorError}
            />
            {showDOBValidatorError && (
              <div className="rewards-club-signup-form_form-field-error">
                Please enter a valid date
              </div>
            )}
          </div>
          <div className="rewards-club-signup-form_consent-form-field">
            <CheckBox
              label={`I agree to receive information, deals and offers from ${brandName}.`}
              checked={formState.consent}
              onChange={handleConsentCheckChange}
            />
            {showConsentError && (
              <div className="rewards-club-signup-form_form-field-error">
                Please provide your consent before proceeding
              </div>
            )}
          </div>
          <div className="rewards-club-signup-form_submit">
            <Button content="JOIN REWARDS CLUB" onClick={handleFormSubmit} />
          </div>
        </div>
      </div>
      {showVerifyOTPPopup && (
        <AuthenticationPopup
          openSegmentPopup={showVerifyOTPPopup}
          dismissSegmentPopup={() => setShowVerifyOTPPopup(false)}
          phoneNumber={formState.phoneNumber}
          onPhoneNumberChangeClick={handlePhoneNumberChangeClick}
          onOTPVerify={handleOTPVerify}
          onlyVerifyOTP
        />
      )}
      {showOTPErrorPopup && (
        <ExceptionComponent
          pageType="HalfSize"
          openSegmentPopup={showOTPErrorPopup}
          dismissSegmentPopup={handleOTPErrorPopupClose}
          title="Incorrect OTP"
          description="Please recheck the OTP that you have entered"
          buttonCta="BACK"
          handleButtonCta={handleOTPErrorPopupConfirm}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  brand: get(state, 'common.appStartupInfo.brand', {}),
  errorResponse: get(state, 'common.errorResponse', {}),
});

const mapDispatchToProps = {
  sendOtp: sendOtpAction,
  attemptLogin: attemptLoginAction,
  updateProfile: updateProfileAction,
  addBrandGuest: addBrandGuestAction,
  dispatchIsRewardsClubFlow: dispatchIsRewardsClubFlowAction,
  clearApiErrorResponse: clearApiErrorResponseAction,
  fetchAppStartupInfo: fetchAppStartupInfoAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(RewardsClubSignup);
